import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscribeToExpiredLinkModal,
  closeExpiredLinkModal,
  setExpiredLinkModalStatus,
} from '../../services/expiredLinkModalService';
import { resendSecretLink } from '../../services/authService';
import { logError } from '../../services/loggerService';
import { Modal } from './Modal';

/**
 * Modal that pops when /login-secret returns 401 for a `?s=<secret>` URL.
 * Pure "would you like a fresh link?" UX — the BE re-sends via the channel
 * (email or SMS) the original notification used, so we never have to ask
 * the user where to send it. Once they click "Resend Link" successfully,
 * they land here and pick the new link up from their inbox/phone.
 */
export const ExpiredLinkModal: React.FC = () => {
  const { t } = useTranslation('common');
  const [state, setState] = useState(() => ({
    isOpen: false,
    status: 'idle' as 'idle' | 'sending' | 'sent' | 'error',
    errorMessage: undefined as string | undefined,
    expiredSecret: undefined as string | undefined,
  }));

  useEffect(() => {
    return subscribeToExpiredLinkModal(setState);
  }, []);

  const handleResend = async () => {
    if (!state.expiredSecret) return;
    setExpiredLinkModalStatus('sending');
    try {
      await resendSecretLink(state.expiredSecret);
      setExpiredLinkModalStatus('sent');
    } catch (err) {
      logError(
        err instanceof Error ? err : new Error(String(err)),
        'Failed to resend secret link',
      );
      const status = (err as { status?: number }).status;
      // 403 = not renewable (no channel to resend on), 404 = no notification
      // back-reference. Either way the user can't recover via resend, so we
      // surface a helpful message and let them sign in normally.
      if (status === 403 || status === 404) {
        setExpiredLinkModalStatus('error', t('ui.expiredLink.errorNotRenewable'));
      } else {
        setExpiredLinkModalStatus('error', t('ui.expiredLink.errorGeneric'));
      }
    }
  };

  return (
    <Modal isOpen={state.isOpen} onClose={closeExpiredLinkModal} size="md" zIndexClass="z-50">
      <h2 className="text-lg font-bold text-foreground mb-2">
          {state.status === 'sent' ? t('ui.expiredLink.titleSent') : t('ui.expiredLink.titleExpired')}
        </h2>

        {state.status === 'idle' && (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {t('ui.expiredLink.bodyExpired')}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={closeExpiredLinkModal}
                className="px-4 py-2 text-sm font-medium text-foreground bg-secondary rounded-lg hover:opacity-90 transition-colors"
              >
                {t('actions.cancel')}
              </button>
              <button
                onClick={handleResend}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors"
              >
                {t('ui.expiredLink.resendCta')}
              </button>
            </div>
          </>
        )}

        {state.status === 'sending' && (
          <p className="text-sm text-muted-foreground mb-4">{t('ui.expiredLink.sending')}</p>
        )}

        {state.status === 'sent' && (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {t('ui.expiredLink.bodySent')}
            </p>
            <div className="flex justify-end">
              <button
                onClick={closeExpiredLinkModal}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors"
              >
                {t('actions.close')}
              </button>
            </div>
          </>
        )}

        {state.status === 'error' && (
          <>
            <p className="text-sm text-muted-foreground mb-4">{state.errorMessage}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={closeExpiredLinkModal}
                className="px-4 py-2 text-sm font-medium text-foreground bg-secondary rounded-lg hover:opacity-90 transition-colors"
              >
                {t('actions.close')}
              </button>
              <button
                onClick={handleResend}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors"
              >
                {t('actions.retry')}
              </button>
            </div>
          </>
        )}
    </Modal>
  );
};
